import React from 'react';
import ReactDOM from 'react-dom';

// Types for the table component
export interface TableColumn<T> {
    key: string;
    label: string;
    render?: (value: any, row: T, index?: number) => React.ReactNode;
    /**
     * Optional extractor for the sort value.
     * Use this when the column uses a `render` function and the raw
     * `row[key]` value is not what you want to sort on.
     * e.g. sortValue: (row) => row.person.fullName
     */
    sortValue?: (row: T) => any;
    className?: string;
    sortable?: boolean; // set to false to disable sorting on this column
}

export interface DropdownItem<T> {
    icon?: React.ReactNode | ((row: T) => React.ReactNode);
    label: string | ((row: T) => string);
    onClick: (row: T) => void;
    className?: string | ((row: T) => string);
    disabled?: boolean | ((row: T) => boolean);
}

export interface TableAction<T> {
    icon?: React.ReactNode;
    label?: string | ((row: T) => string);
    onClick: (row: T, event?: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string | ((row: T) => string);
    title?: string;
    variant?: 'icon' | 'button' | 'dropdown';
    dropdownItems?: DropdownItem<T>[];
}

interface ReusableTableProps<T> {
    columns: TableColumn<T>[];
    data: T[];
    actions?: TableAction<T>[];
    getActions?: (row: T) => TableAction<T>[]; // per-row dynamic actions
    loading?: boolean;
    searchPlaceholder?: string;
    onSearch?: (query: string) => void;
    emptyMessage?: string;
    title?: string;
    subTitle?: string;
    showSearch?: boolean;
    showFilter?: boolean;
    filterPanel?: React.ReactNode;
    onFilterToggle?: () => void;
    isFilterOpen?: boolean;
}

// Portal dropdown — rendered into document.body, completely escapes any overflow/clip context
interface PortalDropdownProps<T> {
    items: DropdownItem<T>[];
    row: T;
    anchorRect: DOMRect;
    onClose: () => void;
}

function PortalDropdown<T>({ items, row, anchorRect, onClose }: PortalDropdownProps<T>) {
    const menuRef = React.useRef<HTMLDivElement>(null);
    const [style, setStyle] = React.useState<React.CSSProperties>({ visibility: 'hidden', position: 'fixed', zIndex: 99999 });

    React.useLayoutEffect(() => {
        const menu = menuRef.current;
        if (!menu) return;

        const menuHeight = menu.offsetHeight;
        const menuWidth = menu.offsetWidth;
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const openUpward = spaceBelow < menuHeight + 8;

        const top = openUpward
            ? anchorRect.top - menuHeight - 4
            : anchorRect.bottom + 4;

        let left = anchorRect.right - menuWidth;
        if (left < 8) left = 8;
        if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;

        setStyle({ position: 'fixed', top, left, zIndex: 99999, visibility: 'visible' });
    }, [anchorRect]);

    React.useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleScroll = () => onClose();

        const t = setTimeout(() => {
            document.addEventListener('mousedown', handleOutside);
            window.addEventListener('scroll', handleScroll, true);
        }, 0);

        return () => {
            clearTimeout(t);
            document.removeEventListener('mousedown', handleOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [onClose]);

    return ReactDOM.createPortal(
        <div ref={menuRef} style={style} className="w-48 bg-white rounded-md shadow-lg border border-gray-200">
            <div className="py-1">
                {items.map((item, i) => {
                    const resolvedLabel = typeof item.label === 'function' ? item.label(row) : item.label;
                    const resolvedIcon = typeof item.icon === 'function' ? item.icon(row) : item.icon;
                    const resolvedClassName = typeof item.className === 'function' ? item.className(row) : item.className;
                    const resolvedDisabled = typeof item.disabled === 'function' ? item.disabled(row) : item.disabled;
                    return (
                        <button
                            key={i}
                            type="button"
                            disabled={resolvedDisabled}
                            onClick={() => { if (resolvedDisabled) return; item.onClick(row); onClose(); }}
                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${resolvedDisabled ? 'text-gray-300 cursor-not-allowed' : `hover:bg-gray-100 ${resolvedClassName || 'text-gray-700'}`}`}
                        >
                            {resolvedIcon}
                            {resolvedLabel}
                        </button>
                    );
                })}
            </div>
        </div>,
        document.body
    );
}

// Sort direction type
type SortDirection = 'asc' | 'desc' | null;

// Sort icon component
function SortIcon({ direction }: { direction: SortDirection }) {
    return (
        <span className="inline-flex flex-col ml-[4px] gap-[1px] opacity-60" aria-hidden="true">
            <svg
                width="8" height="5" viewBox="0 0 8 5" fill="none"
                style={{ opacity: direction === 'asc' ? 1 : 0.35 }}
            >
                <path d="M4 0L7.46 4.5H0.54L4 0Z" fill="currentColor" />
            </svg>
            <svg
                width="8" height="5" viewBox="0 0 8 5" fill="none"
                style={{ opacity: direction === 'desc' ? 1 : 0.35 }}
            >
                <path d="M4 5L0.54 0.5H7.46L4 5Z" fill="currentColor" />
            </svg>
        </span>
    );
}

function ReusableTable<T extends Record<string, any>>({
    columns,
    data,
    actions = [],
    getActions,
    loading = false,
    searchPlaceholder = "Search...",
    onSearch,
    emptyMessage = "No data available",
    title = "Table",
    subTitle = "",
    showSearch = true,
    showFilter = false,
    filterPanel,
    onFilterToggle,
    isFilterOpen = false
}: ReusableTableProps<T>) {
    const [searchQuery, setSearchQuery] = React.useState("");
    const [openDropdown, setOpenDropdown] = React.useState<{ rowIndex: number; rect: DOMRect } | null>(null);
    const [sortKey, setSortKey] = React.useState<string | null>(null);
    const [sortDirection, setSortDirection] = React.useState<SortDirection>(null);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (onSearch) onSearch(value);
    };

    const handleColumnSort = (column: TableColumn<T>) => {
        if (column.sortable === false) return;

        if (sortKey === column.key) {
            if (sortDirection === 'asc') setSortDirection('desc');
            else if (sortDirection === 'desc') { setSortDirection(null); setSortKey(null); }
            else setSortDirection('asc');
        } else {
            setSortKey(column.key);
            setSortDirection('asc');
        }
    };

    const sortedData = React.useMemo(() => {
        if (!sortKey || !sortDirection) return data;

        // Find the column so we can use its sortValue extractor if present
        const column = columns.find(c => c.key === sortKey);

        return [...data].sort((a, b) => {
            // Use sortValue extractor if provided, otherwise fall back to row[key]
            const aVal = column?.sortValue ? column.sortValue(a) : a[sortKey];
            const bVal = column?.sortValue ? column.sortValue(b) : b[sortKey];

            // Nulls last
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            // Numeric comparison
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // String / everything else
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();

            if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
            if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, sortKey, sortDirection, columns]);

    const handleTriggerClick = (rowIndex: number, e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (openDropdown?.rowIndex === rowIndex) {
            setOpenDropdown(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setOpenDropdown({ rowIndex, rect });
    };

    const renderAction = (action: TableAction<T>, row: T, rowIndex: number) => {
        if (action.variant === 'dropdown' && action.dropdownItems) {
            return (
                <div className="relative inline-block">
                    <button
                        type="button"
                        onClick={(e) => handleTriggerClick(rowIndex, e)}
                        className="bg-transparent border-none py-[4px] px-[8px] cursor-pointer text-[#6b7280] rounded-[4px] transition-all duration-200 hover:bg-[#f3f4f6] hover:text-[#111827]"
                        title={action.title || "More options"}
                    >
                        {action.icon || (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <circle cx="10" cy="4" r="1.5" />
                                <circle cx="10" cy="10" r="1.5" />
                                <circle cx="10" cy="16" r="1.5" />
                            </svg>
                        )}
                    </button>
                    {openDropdown?.rowIndex === rowIndex && (
                        <PortalDropdown
                            items={action.dropdownItems}
                            row={row}
                            anchorRect={openDropdown.rect}
                            onClose={() => setOpenDropdown(null)}
                        />
                    )}
                </div>
            );
        }

        if (action.variant === 'button' || action.label) {
            const label = typeof action.label === 'function' ? action.label(row) : action.label;
            const resolvedClassName = typeof action.className === 'function' ? action.className(row) : action.className;
            const finalClassName = resolvedClassName || 'px-3 py-1.5 text-sm font-medium rounded-md transition-colors hover:opacity-80';
            return (
                <button
                    type="button"
                    onClick={(e) => action.onClick(row, e)}
                    className={finalClassName}
                    title={action.title}
                >
                    {action.icon && <span className="inline-block mr-1">{action.icon}</span>}
                    {label}
                </button>
            );
        }

        const iconClassName = typeof action.className === 'function'
            ? action.className(row)
            : (action.className || 'bg-transparent border-none py-[4px] px-[8px] cursor-pointer text-[#6b7280] text-[18px] rounded-[4px] transition-all duration-200 hover:bg-[#f3f4f6] hover:text-[#111827]');
        return (
            <button
                type="button"
                onClick={(e) => action.onClick(row, e)}
                className={iconClassName}
                title={action.title}
            >
                {action.icon}
            </button>
        );
    };

    return (
        <div>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .animate-spin { animation: spin 0.8s linear infinite; }
                .sortable-th { cursor: pointer; user-select: none; }
                .sortable-th:hover { background-color: #f3f4f6; }
            `}</style>

            <div className="bg-white rounded-[8px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.1)] px-[16px] sm:px-[24px] py-[16px] sm:py-[20px]">

                <div className="mb-[20px] sm:mb-[24px] flex flex-col md:flex-row md:items-center md:justify-between gap-[12px]">
                    <div>
                        <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#0A0A0A] m-0 mb-1">{title}</h2>
                        {subTitle && <p className="text-xs sm:text-sm text-gray-500">{subTitle}</p>}
                    </div>

                    {(showSearch || showFilter) && (
                        <div className="flex flex-col sm:flex-row gap-[12px] sm:items-center">
                            {showSearch && (
                                <div className="relative w-full sm:w-[280px]">
                                    <svg className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[#717182] pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={handleSearch}
                                        placeholder={searchPlaceholder}
                                        className="w-full py-[8px] px-[12px] pl-[36px] bg-[#F8FAFC] border border-transparent rounded-[6px] text-[14px] outline-none transition-[border-color] duration-200 focus:border-[#717182] placeholder:text-[#717182]"
                                    />
                                </div>
                            )}
                            {showFilter && (
                                <button
                                    onClick={onFilterToggle}
                                    className={`py-[8px] px-[12px] border rounded-[6px] flex items-center justify-center gap-[6px] cursor-pointer text-[14px] transition-all duration-200 w-full sm:w-auto ${isFilterOpen ? 'bg-green-700 text-white border-green-700 hover:bg-green-800' : 'bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb] hover:border-[#d1d5db]'}`}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2 4H14M4 8H12M6 12H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                    Filter
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {showFilter && isFilterOpen && filterPanel && (
                    <div className="mb-6">{filterPanel}</div>
                )}

                <div className="overflow-x-auto -mx-[16px] sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden p-4">
                            <table className="min-w-full border-collapse">
                                <thead>
                                    <tr>
                                        {columns.map((column) => {
                                            const isSortable = column.sortable !== false;
                                            const isActive = sortKey === column.key;
                                            return (
                                                <th
                                                    key={column.key}
                                                    onClick={isSortable ? () => handleColumnSort(column) : undefined}
                                                    className={`py-[12px] px-[8px] text-left text-[12px] sm:text-[14px] font-[700] text-[#0A0A0A] border-b border-[#0000001A] whitespace-nowrap transition-colors duration-150 ${column.className || ''} ${isSortable ? 'sortable-th' : ''} ${isActive ? 'bg-[#f3f4f6]' : ''}`}
                                                    title={isSortable ? `Sort by ${column.label}` : undefined}
                                                    aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                                                >
                                                    <span className="inline-flex items-center">
                                                        {column.label}
                                                        {isSortable && (
                                                            <SortIcon direction={isActive ? sortDirection : null} />
                                                        )}
                                                    </span>
                                                </th>
                                            );
                                        })}
                                        {actions.length > 0 && (
                                            <th className="py-[12px] px-[8px] text-left text-[12px] sm:text-[14px] font-[700] text-[#0A0A0A] border-b border-[#0000001A] whitespace-nowrap">
                                                Actions
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)}>
                                                <div className="py-[60px] px-[24px] text-center flex justify-center items-center gap-[12px]">
                                                    <div className="w-[20px] h-[20px] border-2 border-[#e5e7eb] border-t-[#3b82f6] rounded-full animate-spin"></div>
                                                    <span className="text-sm sm:text-base">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : sortedData.length === 0 ? (
                                        <tr>
                                            <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)}>
                                                <div className="py-[60px] px-[24px] text-center text-[#6b7280] text-sm sm:text-base">{emptyMessage}</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedData.map((row, rowIndex) => {
                                            const rowActions = getActions ? getActions(row) : actions;
                                            return (
                                            <tr key={rowIndex} className="hover:bg-[#f9fafb]">
                                                {columns.map((column) => (
                                                    <td key={column.key} className={`py-[6px] px-[8px] text-[12px] sm:text-[14px] text-[#111827] border-t border-[#0000001A] ${column.className || ''}`}>
                                                        {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                                                    </td>
                                                ))}
                                                {rowActions.length > 0 && (
                                                    <td className="py-[6px] px-[8px] text-[12px] sm:text-[14px] text-[#111827] border-t border-[#0000001A]">
                                                        <div className="flex gap-[4px] sm:gap-[8px] items-center">
                                                            {rowActions.map((action, actionIndex) => (
                                                                <React.Fragment key={actionIndex}>
                                                                    {renderAction(action, row, rowIndex)}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {data.length > 0 && !loading && (
                    <div className="mt-3 text-center text-xs text-gray-400 sm:hidden">
                        ← Scroll to see more →
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReusableTable;